// -----------------------------------------------------------------------------
// Overlay legend showing the active layer's color gradient and min/max values
// with thresholds. Renders gradient/ticks via a tiny WebGL2 shader.
//
// Spec implemented:
// - BAR = layer COLOR DOMAIN [dMin,dMax] when UNMAPPED.
// - TICKS = positions in FRAME space (clamped to frame∩domain), always drawn.
// - MAP toggle = segmentsPanel.isLocal (from getSegments()).
//   When turned on the color domain is based on the getMappedRange() array [Min, Max]
// - Labels: show the current tick values (thrLoVis, thrHiVis) amd the unit of measure in
//   in the middle.
// -----------------------------------------------------------------------------

import React from 'react';
import { useTheme, alpha } from '@mui/material';

// Store Zustand
import usePersistentStore, { getColorScheme, useGlobalBucket, useLayersBucket } from '@gui/store/persistent/persistentStore';
import { useSegmentsSlice } from '@gui/store/hooks';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { isEnergyLabel, isTimeLabel } from '@gui/constants/Data';
import { SIM_NOISE, SIM_SUN, SIM_SUN4D, SIM_TARGET, SIM_VVA, SIM_WIND } from '@visualizer/types';
import { getSimTooltip } from 'Simulations/SimConfig';
import { getGrid } from 'store/LoadingStore';
import { formatMetricPair } from 'Utils/MetricFormatting';

// CSS
import '../../style.css';

// ------------------------- Types ---------------------------------------------
type Corner = 'bl' | 'br' | 'tl' | 'tr';
const PERCENT_NORMALIZED_TOLERANCE = 0.001;

// ------------------------- Minimal WebGL shaders ------------------------------
const VS = `#version 300 es
in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;        // NDC [-1,1] -> [0,1]
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FS = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 frag;

uniform float uHue1, uHue2, uSat, uLum; // hue deg, S/L in 0..100
uniform float uTMin, uTMax;             // tick positions in CANVAS space [0..1]
uniform float uMapMin, uMapMax;         // mapped interval (set after MAP freeze)
uniform float uLineW;                   // fraction of width (for ~1px AA)

vec3 hsl2rgb(float h, float s, float l){
  h = mod(h, 360.0) / 360.0;
  s = clamp(s/100.0, 0.0, 1.0);
  l = clamp(l/100.0, 0.0, 1.0);
  float c = (1.0 - abs(2.0*l - 1.0)) * s;
  float x = c * (1.0 - abs(mod(h * 6.0, 2.0) - 1.0));
  vec3 rgb;
  if      (h < 1.0/6.0) rgb = vec3(c, x, 0.0);
  else if (h < 2.0/6.0) rgb = vec3(x, c, 0.0);
  else if (h < 3.0/6.0) rgb = vec3(0.0, c, x);
  else if (h < 4.0/6.0) rgb = vec3(0.0, x, c);
  else if (h < 5.0/6.0) rgb = vec3(x, 0.0, c);
  else                  rgb = vec3(c, 0.0, x);
  float m = l - 0.5 * c;
  return rgb + vec3(m);
}

void main() {
  float t = clamp(vUV.x, 0.0, 1.0);

  // thresholds (for tick lines only)
  float tTick0 = min(uTMin, uTMax);
  float tTick1 = max(uTMin, uTMax);

  // mapped range (for gradient compression + grey outside)
  float t0 = min(uMapMin, uMapMax);
  float t1 = max(uMapMin, uMapMax);
  float range = max(1e-6, t1 - t0);

  // compress hue span inside mapped interval
  float tNorm = clamp((t - t0) / range, 0.0, 1.0);
  float span = mod(uHue2 - uHue1 + 360.0, 360.0);
  float tColor = 1.0 - tNorm;                      // reverse direction if desired
  float H = mod(uHue1 + span * tColor, 360.0);
  vec3 base = hsl2rgb(H, uSat, uLum);

  // grey outside mapped interval
  vec3 grey = hsl2rgb(0.0, 0.0, uLum);
  bool outside = (t < t0) || (t > t1);
  vec3 col = mix(base, grey, float(outside));

  // tick marks: white halo (outer), black core (inner)
  float d1 = abs(t - tTick0);
  float d2 = abs(t - tTick1);

  // outer (a bit wider) -> white
  float outer1 = 1.0 - smoothstep(uLineW * 1.5, uLineW * 3.0, d1);
  float outer2 = 1.0 - smoothstep(uLineW * 1.5, uLineW * 3.0, d2);
  float outer  = clamp(outer1 + outer2, 0.0, 1.0);

  // inner (narrow) -> black
  float inner1 = 1.0 - smoothstep(uLineW, uLineW * 2.0, d1);
  float inner2 = 1.0 - smoothstep(uLineW, uLineW * 2.0, d2);
  float inner  = clamp(inner1 + inner2, 0.0, 1.0);

  // compose: first white outline, then black center
  col = mix(col, vec3(1.0), outer);
  col = mix(col, vec3(0.0), inner);

  frag = vec4(col, 1.0);
}
`;

// ------------------------- Utils ---------------------------------------------

const formatLegendPair = (minVal: number, maxVal: number) => {
    const { min, max } = formatMetricPair(minVal, maxVal);
    return { minStr: min, maxStr: max };
};

const getLegendDescription = (simKind: number, frameCount: number, resultLabel?: string) => {
    const sunMetric = isEnergyLabel(resultLabel) ? 'energy' : isTimeLabel(resultLabel) ? 'time' : '';
    switch (simKind) {
        case SIM_SUN:
            return `Yearly solar ${sunMetric}`;
        case SIM_SUN4D:
            return frameCount === 24 ? `Hourly solar ${sunMetric}` : `Monthly solar ${sunMetric}`;
        case SIM_VVA:
            return 'Portion of the visibility shape';
        case SIM_TARGET:
            return 'Portion of the spherical view';
        case SIM_NOISE:
            return 'Sound pressure levels';
        case SIM_WIND:
            return 'Wind speed';
        default:
            return undefined;
    }
};

const buildUnitLabel = (unit?: string) => {
    return unit ? `${unit}` : '';
};

const isPercentUnit = (unit?: string) => (unit ?? '').replace(/\s/g, '').includes('%');

const isNormalizedPercentRange = (unit: string | undefined, min: number, max: number) =>
    isPercentUnit(unit) &&
    Number.isFinite(min) &&
    Number.isFinite(max) &&
    min >= -PERCENT_NORMALIZED_TOLERANCE &&
    max <= 1 + PERCENT_NORMALIZED_TOLERANCE;

// ------------------------- Component -----------------------------------------
export const OverlayLegend: React.FC<{
    corner?: Corner;
    width?: number;
    barHeight?: number;
    padding?: number;
}> = ({ corner = 'br', width = 260, barHeight = 10, padding = 6 }) => {
    const { segments } = useSegmentsSlice();
    const { legendVisible, absoluteMinMax, remappedMinMax, tmpMinMax } = useGlobalBucket((s) => s.overlays);
    const activeLayerID = useLayersBucket((s) => s.activeLayerID);
    const activeResultId = usePersistentStore((s) => s.getActiveLayerState()?.data?.activeResultId ?? 0);
    const segmentsFrameMax = segments?.frameMax ?? 0;

    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const labelsRef = React.useRef<HTMLDivElement | null>(null);

    const { tooltipMessage, legendDescription } = React.useMemo(() => {
        const layer = getGrid?.()?.getActiveLayer?.();
        if (!layer) return { tooltipMessage: undefined, legendDescription: undefined };
        const frames = layer.getFrameCollection?.() ?? [];
        const simKind = layer.getSimKind();
        const frameCount = Math.max(frames.length ?? 0, segmentsFrameMax);
        const resultLabel = layer.getResult?.()?.resultLabel;
        return {
            tooltipMessage: getSimTooltip(simKind, frameCount),
            legendDescription: getLegendDescription(simKind, frameCount, resultLabel),
        };
    }, [activeLayerID, activeResultId, segmentsFrameMax]);

    const basePos: React.CSSProperties =
        corner === 'br'
            ? { right: 'calc(16px + var(--right-drawer-visible, 0px))', bottom: 16 }
            : corner === 'tr'
              ? { right: 'calc(16px + var(--right-drawer-visible, 0px))', top: 16 }
              : corner === 'bl'
                ? { left: 16, bottom: 16 }
                : { left: 16, top: 16 };

    React.useEffect(() => {
        if (!legendVisible) return;

        const canvas = canvasRef.current!;
        canvas.style.background = 'transparent';

        // ---- layout sizing inside the legend card --------------------------------
        const gap = 4;
        const labelH = 24;
        const contentW_css = width - padding * 2;
        const contentH_css = barHeight + gap + labelH;

        const setCanvasSize = () => {
            const dpr = Math.max(1, window.devicePixelRatio || 1);
            canvas.style.width = `${contentW_css}px`;
            canvas.style.height = `${contentH_css}px`;
            canvas.width = Math.round(contentW_css * dpr);
            canvas.height = Math.round(contentH_css * dpr);
        };
        setCanvasSize();

        // ---- WebGL2 setup ---------------------------------------------------------
        const gl = canvas.getContext('webgl2', {
            alpha: true,
            premultipliedAlpha: false,
            antialias: false,
            preserveDrawingBuffer: true,
        });
        if (!gl) return;

        const compile = (type: number, src: string) => {
            const sh = gl.createShader(type)!;
            gl.shaderSource(sh, src);
            gl.compileShader(sh);
            if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
                throw new Error(gl.getShaderInfoLog(sh) || 'shader compile error');
            }
            return sh;
        };
        const vs = compile(gl.VERTEX_SHADER, VS);
        const fs = compile(gl.FRAGMENT_SHADER, FS);
        const prog = gl.createProgram()!;
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            throw new Error(gl.getProgramInfoLog(prog) || 'program link error');
        }
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        gl.useProgram(prog);

        // Fullscreen quad (two triangles)
        const aPos = gl.getAttribLocation(prog, 'aPos');
        const buf = gl.createBuffer()!;
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, -1, 1, 1, -1, 1]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(aPos);
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

        // Uniforms
        const uHue1 = gl.getUniformLocation(prog, 'uHue1')!;
        const uHue2 = gl.getUniformLocation(prog, 'uHue2')!;
        const uMapMin = gl.getUniformLocation(prog, 'uMapMin')!;
        const uMapMax = gl.getUniformLocation(prog, 'uMapMax')!;
        const uSat = gl.getUniformLocation(prog, 'uSat')!;
        const uLum = gl.getUniformLocation(prog, 'uLum')!;
        const uTMin = gl.getUniformLocation(prog, 'uTMin')!;
        const uTMax = gl.getUniformLocation(prog, 'uTMax')!;
        const uLineW = gl.getUniformLocation(prog, 'uLineW')!;

        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);

        // Labels
        const writeLabels = (minVal: number, maxVal: number, description?: string, unitLabel?: string) => {
            const wrap = labelsRef.current!;
            const { minStr, maxStr } = formatLegendPair(minVal, maxVal);
            wrap.querySelector<HTMLDivElement>('.legend-min')!.textContent = minStr;
            wrap.querySelector<HTMLDivElement>('.legend-max')!.textContent = maxStr;

            const comboEl = wrap.querySelector<HTMLDivElement>('.legend-combo')!;
            const combined = [description, unitLabel].filter(Boolean).join(' ');
            comboEl.textContent = combined;
            comboEl.style.visibility = combined ? 'visible' : 'hidden';
        };

        // inner padding so the bar doesn't touch rounded ends
        const barSidePad = 6;
        const barUsableW_css = Math.max(1, contentW_css - barSidePad * 2);

        const render = () => {
            gl.clear(gl.COLOR_BUFFER_BIT);

            const grid = getGrid?.();
            const layer = grid?.getActiveLayer?.();
            if (!layer) {
                return;
            }

            const frame = layer.getActiveFrame();
            if (!frame) {
                return;
            }

            // FRAME range tick movement space
            const fMin = tmpMinMax[0];
            const fMax = tmpMinMax[1];
            if (!Number.isFinite(fMin) || !Number.isFinite(fMax)) {
                return;
            }
            const hasCollapsedFrameRange = fMax <= fMin;

            // LEGEND COLOR DOMAIN bar space
            const { min: dMin, max: dMax } = { min: absoluteMinMax[0], max: absoluteMinMax[1] };
            const dSpan = Math.max(1e-12, dMax - dMin);

            // Thick position
            const fr = { min: tmpMinMax[0], max: tmpMinMax[1] };
            const thickLow = fr.min;
            const thickHigh = fr.max;

            // ---- Palette uniforms ---------------------------------------------------
            const cs = getColorScheme();
            const dpr = Math.max(1, window.devicePixelRatio || 1);
            gl.useProgram(prog);
            gl.uniform1f(uHue1, (cs.GRID_FILL_H?.[0] ?? 0) * 3.6); // 0..100 → 0..360
            gl.uniform1f(uHue2, (cs.GRID_FILL_H?.[1] ?? 100) * 3.6);
            gl.uniform1f(uSat, (cs.GRID_FILL_S ?? 1) * 100); // 0..1   → 0..100
            gl.uniform1f(uLum, (cs.GRID_FILL_L ?? 0.5) * 100);

            // ---- Bar geometry in device pixels + helpers ---------------------------
            const scX = Math.round(barSidePad * dpr);
            const scY = Math.round((contentH_css - barHeight) * dpr);
            const scW = Math.round(barUsableW_css * dpr);
            const scH = Math.round(barHeight * dpr);

            // map t∈[0..1] (bar inner) → canvas fraction [0..1]
            const tInnerToCanvas = (t: number) => (scX + t * scW) / canvas.width;
            // avoid exact edge to keep AA nice
            const epsU = (0.5 * dpr) / canvas.width;

            // value (domain space) → [0..1] in domain → canvas fraction
            const to01 = (v: number) => Math.min(1, Math.max(0, (v - dMin) / dSpan));
            const toFx = (n: number) => Math.min(1 - epsU, Math.max(epsU, tInnerToCanvas(n)));

            // --- Tick padding so edges never clip ---
            const uLineWVal = (1.0 * dpr) / canvas.width;
            const tickPadInner = Math.min(0.5, (1.5 * uLineWVal * canvas.width) / scW); // = (3*dpr)/scW

            const clampInner = (tInner: number) => Math.min(1 - tickPadInner, Math.max(tickPadInner, tInner));

            // Map window for drawing (unchanged)
            const mapLoForDraw = remappedMinMax[0];
            const mapHiForDraw = remappedMinMax[1];

            // Compute inner positions, clamp away from edges, THEN map to canvas
            const tInnerMin = clampInner(to01(thickLow));
            const tInnerMax = hasCollapsedFrameRange ? tInnerMin : clampInner(to01(thickHigh));

            // Send uniforms
            gl.uniform1f(uMapMin, toFx(to01(mapLoForDraw)));
            gl.uniform1f(uMapMax, toFx(to01(mapHiForDraw)));
            gl.uniform1f(uTMin, toFx(tInnerMin));
            gl.uniform1f(uTMax, toFx(tInnerMax));
            gl.uniform1f(uLineW, uLineWVal);

            // Draw only inside the bar rect
            gl.enable(gl.SCISSOR_TEST);
            gl.scissor(scX, scY, scW, scH);
            gl.drawArrays(gl.TRIANGLES, 0, 6);
            gl.disable(gl.SCISSOR_TEST);

            // Labels
            const frames = layer.getFrameCollection?.() ?? [];
            const frameCount = Math.max(frames.length ?? 0, segmentsFrameMax);
            const resultLabel = layer.getResult?.()?.resultLabel;
            const description = legendDescription ?? getLegendDescription(layer.getSimKind(), frameCount, resultLabel);
            const unit = layer.getResult()?.unitOfMeasure;
            const unitLabel = buildUnitLabel(unit);
            const displaysNormalizedPercent = isNormalizedPercentRange(unit, absoluteMinMax[0], absoluteMinMax[1]);

            // show domain when NOT local, frame when local
            const leftLabel = displaysNormalizedPercent ? absoluteMinMax[0] * 100 : absoluteMinMax[0];
            const rightLabel = displaysNormalizedPercent ? absoluteMinMax[1] * 100 : absoluteMinMax[1];

            writeLabels(leftLabel, rightLabel, description, unitLabel);
        };

        // call once after setup
        render();

        // Resize handler keeps DPR crisp
        const onResize = () => {
            setCanvasSize();
            gl.viewport(0, 0, canvas.width, canvas.height);
            render();
        };
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [
        legendVisible,
        absoluteMinMax,
        remappedMinMax,
        tmpMinMax,
        width,
        barHeight,
        padding,
        activeLayerID,
        segmentsFrameMax,
        legendDescription,
        activeResultId,
    ]);

    // ------------------------- Theme ----------------------------------------------
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    // Drawer
    const columnBg = alpha(theme.palette.background.default, isLight ? 0.45 : 0.3);
    const columnBlurPx = 10;

    // Panel
    const panelBg = alpha(theme.palette.background.paper, isLight ? 0.26 : 0.5);
    const panelBorder = isLight ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.common.white, 0.08);
    const panelColor = theme.palette.text.primary;

    if (!legendVisible) return null;

    // ------------------------- UI ----------------------------------------------
    return (
        <div style={{ position: 'fixed', zIndex: 2000, pointerEvents: 'none', ...basePos }}>
            {/* Drawer-like underlay (same as column paper) */}
            <div
                style={{
                    display: 'inline-block',
                    borderRadius: 12,
                    background: columnBg,
                    backdropFilter: `blur(${columnBlurPx}px)`,
                    WebkitBackdropFilter: `blur(${columnBlurPx}px)`,
                    padding: 0,
                }}
            >
                {/* Actual legend panel */}
                <div
                    className="data-panel overlay-legend"
                    style={{
                        position: 'relative',
                        display: 'block',
                        width,
                        padding,
                        borderRadius: 10,
                        background: panelBg,
                        border: `1px solid ${panelBorder}`,
                        color: panelColor,
                    }}
                >
                    <canvas ref={canvasRef} />
                    <div
                        ref={labelsRef}
                        style={{
                            position: 'absolute',
                            left: padding,
                            right: padding,
                            top: padding + barHeight + 2,
                            color: 'inherit',
                        }}
                    >
                        <div
                            className="legend-min"
                            style={{
                                position: 'absolute',
                                left: 6,
                                top: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'left',
                                fontSize: 10,
                                lineHeight: '14px',
                                maxWidth: '45%',
                            }}
                        />
                        <CustomTooltip
                            side="right"
                            title={tooltipMessage}
                            popperSx={{ zIndex: 24000 }}
                            placement="top"
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: { offset: [-2, 18] },
                                        },
                                    ],
                                },
                            }}
                        >
                            <div
                                className="legend-center"
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    width: '100%',
                                    top: 12,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    textAlign: 'center',
                                    pointerEvents: tooltipMessage ? 'auto' : 'none',
                                    maxWidth: '100%',
                                }}
                            >
                                <div
                                    className="legend-combo"
                                    style={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        marginTop: 2,
                                        textOverflow: 'ellipsis',
                                        fontSize: 10,
                                        lineHeight: '14px',
                                        opacity: 0.9,
                                        maxWidth: '100%',
                                    }}
                                />
                            </div>
                        </CustomTooltip>
                        <div
                            className="legend-max"
                            style={{
                                position: 'absolute',
                                right: 6,
                                top: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'right',
                                fontSize: 10,
                                lineHeight: '14px',
                                maxWidth: '45%',
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
